import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { registerPartner } from '../../services/PartnerService';
import { formatKenyanPhoneNumber } from '../../utils/helpers';
import { showMutationError, makeFieldErrorClearer } from '../../utils/showMutationError';

const emptyForm = {
    name: '',
    super_admin: {
        first_name: '',
        last_name: '',
        preferred_name: '',
        email: '',
        phone: '',
        with_minipot: false,
    },
};

const RegisterPartner = ({ show, onHide }) => {
    const queryClient = useQueryClient();

    const [form, setForm] = useState(emptyForm);
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (show) {
            setForm(emptyForm);
            setFieldErrors({});
        }
    }, [show]);

    const clearFieldError = makeFieldErrorClearer(setFieldErrors);

    const setSuperAdminField = (field, value) => {
        setForm((prev) => ({
            ...prev,
            super_admin: { ...prev.super_admin, [field]: value },
        }));
        clearFieldError(`super_admin.${field}`);
    };

    const isSubmittable =
        form.name.trim() &&
        form.super_admin.first_name.trim() &&
        form.super_admin.last_name.trim() &&
        form.super_admin.email.trim();

    const mutation = useMutation({
        mutationFn: () => registerPartner(form),
        onSuccess: (response) => {
            toast.success(response.data.message);
            queryClient.invalidateQueries({ queryKey: ['partners'] });
            onHide();
        },
        onError: (err) => showMutationError(err, {
            fallback: 'Adding partner failed.',
            onFieldErrors: setFieldErrors,
        }),
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        setFieldErrors({});
        mutation.mutate();
    };

    const inputClass = (key) =>
        `form-control${fieldErrors[key] ? ' is-invalid' : ''}`;

    const renderFieldError = (key) =>
        fieldErrors[key] ? (
            <div className="invalid-feedback d-block">{fieldErrors[key]}</div>
        ) : null;

    return (
        <Modal show={show} onHide={onHide} centered>
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title">Add Partner</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={onHide}
                        disabled={mutation.isPending}
                    />
                </div>
                <form onSubmit={handleSubmit} noValidate>
                    <div className="modal-body">
                        <h6 className="fw-bold text-uppercase text-secondary small mb-3 pb-2 border-bottom">Partner Details</h6>
                        <div className="form-group mb-4">
                            <label htmlFor="partnerName">Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={inputClass('name')}
                                id="partnerName"
                                value={form.name}
                                onChange={(e) => {
                                    setForm((prev) => ({ ...prev, name: e.target.value }));
                                    clearFieldError('name');
                                }}
                                autoFocus
                            />
                            {renderFieldError('name')}
                        </div>

                        <h6 className="fw-bold text-uppercase text-secondary small mb-3 mt-4 pb-2 border-bottom">First Super Admin</h6>
                        <div className="row g-3 mb-3">
                            <div className="col-sm-6">
                                <label htmlFor="superAdminFirstName">First Name <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={inputClass('super_admin.first_name')}
                                    id="superAdminFirstName"
                                    value={form.super_admin.first_name}
                                    onChange={(e) => setSuperAdminField('first_name', e.target.value)}
                                />
                                {renderFieldError('super_admin.first_name')}
                            </div>
                            <div className="col-sm-6">
                                <label htmlFor="superAdminLastName">Last Name <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={inputClass('super_admin.last_name')}
                                    id="superAdminLastName"
                                    value={form.super_admin.last_name}
                                    onChange={(e) => setSuperAdminField('last_name', e.target.value)}
                                />
                                {renderFieldError('super_admin.last_name')}
                            </div>
                        </div>
                        <div className="form-group mb-3">
                            <label htmlFor="superAdminPreferredName">
                                Preferred Name <span className="text-muted fw-normal">(optional)</span>
                            </label>
                            <input
                                type="text"
                                className={inputClass('super_admin.preferred_name')}
                                id="superAdminPreferredName"
                                value={form.super_admin.preferred_name}
                                onChange={(e) => setSuperAdminField('preferred_name', e.target.value)}
                            />
                            {renderFieldError('super_admin.preferred_name')}
                        </div>
                        <div className="form-group mb-3">
                            <label htmlFor="superAdminEmail">Email <span className="text-danger">*</span></label>
                            <input
                                type="email"
                                className={inputClass('super_admin.email')}
                                id="superAdminEmail"
                                value={form.super_admin.email}
                                onChange={(e) => setSuperAdminField('email', e.target.value)}
                            />
                            {renderFieldError('super_admin.email')}
                        </div>
                        <div className="form-group mb-3">
                            <label htmlFor="superAdminPhone">Phone <span className="text-muted fw-normal">(optional)</span></label>
                            <input
                                type="text"
                                className={inputClass('super_admin.phone')}
                                id="superAdminPhone"
                                value={form.super_admin.phone}
                                onChange={(e) => setSuperAdminField('phone', formatKenyanPhoneNumber(e.target.value))}
                            />
                            {renderFieldError('super_admin.phone')}
                        </div>
                        <div className="form-group mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="superAdminWithWallet"
                                    checked={!!form.super_admin.with_minipot}
                                    onChange={(e) => setSuperAdminField('with_minipot', e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="superAdminWithWallet">
                                    Give this super admin a wallet
                                </label>
                            </div>
                            <small className="form-text text-muted d-block mt-1">
                                Turn this on if the super admin will also spend money. Leave it off if they only oversee and approve.
                            </small>
                            {renderFieldError('super_admin.with_minipot')}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onHide}
                            disabled={mutation.isPending}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!isSubmittable || mutation.isPending}
                        >
                            {mutation.isPending && <i className="fa fa-spinner fa-spin me-1" />}
                            Add Partner
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default RegisterPartner;
